import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { InviteCreate } from '@tto/shared-models';
import { useInviteUserMutation } from '../../../services/restApi/queries/users.service';
import { toast } from 'react-toastify';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  currentUserRole: string; // To determine if Admin role option should be shown
}

export const InviteUserModal = ({ open, onClose, currentUserRole: _currentUserRole }: InviteUserModalProps) => {
  const [inviteUser, { isLoading }] = useInviteUserMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteCreate>({
    // resolver: zodResolver(InviteUserSchema),
    defaultValues: {
      email: '',
      displayName: '',
    },
  });

  const handleClose = () => {
    if (!isLoading) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data: InviteCreate) => {
    try {
      await inviteUser(data).unwrap();
      toast.success(`Invitation sent to ${data.email}. They will receive an email to set up their account.`, {
        autoClose: 5000,
      });
      reset();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.data?.errorMessage ||
        error?.data ||
        error?.message ||
        error?.errorMessage ||
        'Failed to send invitation';
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Typography variant='h5' component='div' fontWeight={600}>
          Invite User
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          Send an invitation to join the TTO Platform
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box display='flex' flexDirection='column' gap={3}>
            {/* Email Field */}
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Email address'
                  placeholder='name@example.com'
                  type='email'
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                  autoFocus
                />
              )}
            />
            <Controller
              name='displayName'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Team Member Name'
                  placeholder='John Doe'
                  type='text'
                  fullWidth
                  required
                  error={!!errors.displayName}
                  helperText={errors.displayName?.message}
                  disabled={isLoading}
                  autoFocus
                />
              )}
            />

            {/*
              Role Selection (TEMPORARILY DISABLED)
              NOTE: Re-enable this block once role-based invites are implemented.
              It previously allowed selecting User/Admin and used currentUserRole to
              gate the Admin option.
            */}

            {/* Info Alert */}
            <Alert severity='info' variant='outlined'>
              <Typography variant='body2'>
                The user will receive an email invitation with a link to set up their account. The invitation expires in
                72 hours.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} color='inherit'>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={16} />}
          >
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
